import { lazy } from "react";

import { Navigate } from "react-router-dom";

import { AppPagePaths } from "App/routes/paths";

import { convoIntelPathResources } from "./convoIntelPaths";
import ConvoIntelRoutingPage from "./ConvoIntelRoutingPage";

import type { RegalRouteObject } from "App/routes/types";

const TranscriptsList = lazy(() => import("Pages/convoIntel/Pages/RecordingTranscriptsPage/RecordingTranscriptsPage"));
const TranscriptsPage = lazy(
    () => import("Pages/convoIntel/Pages/RecordingTranscriptsPage/Pages/TranscriptItem/TranscriptItem")
);
const TranscriptInsights = lazy(() => import("Pages/convoIntel/Pages/TranscriptInsightsPage/TranscriptInsightsPage"));
const TrackersPage = lazy(() => import("Pages/convoIntel/Pages/TranscriptMomentsPage/TranscriptMomentPage"));
const ScoreCardsList = lazy(() => import("Pages/convoIntel/Pages/ScoreCardTemplatePage/ScoreCardTemplatePage"));
const ScoreCardsEditPage = lazy(
    () => import("Pages/convoIntel/Pages/ScoreCardTemplatePage/Pages/ScoreCardsTemplateEditPage")
);
const CustomAnalysis = lazy(() => import("Pages/convoIntel/Pages/AIAnalysisPage/AIAnalysisPage"));
const ObservabilityPage = lazy(() => import("Pages/convoIntel/Pages/ObservabilityPage/ObservabilityPage"));
const AgentChatsPage = lazy(() => import("Pages/convoIntel/Pages/AgentChatsPage/AgentChatsPage"));
const AgentChatDetail = lazy(() => import("Pages/convoIntel/Pages/AgentChatDetail/AgentChatDetail"));

export type ConvoIntelFlags = {
    showRecordings: boolean;
    showObservability: boolean;
    showMoments: boolean;
    showCustomAnalysis: boolean;
    showScorecards: boolean;
    showInsights: boolean;
};

/**
 * Determines the default redirect path based on available feature flags.
 * Returns the first available page in priority order.
 */
export const getDefaultConvoIntelPath = (flags: ConvoIntelFlags): string => {
    const { showRecordings, showObservability, showMoments, showCustomAnalysis } = flags;

    // Priority order matches the navigation menu order
    if (showRecordings) {
        return `${AppPagePaths.convoIntel}/${convoIntelPathResources.recordings}`;
    }
    if (showObservability) {
        return `${AppPagePaths.convoIntel}/${convoIntelPathResources.observability}`;
    }
    if (showMoments) {
        return `${AppPagePaths.convoIntel}/${convoIntelPathResources.moments}`;
    }
    if (showCustomAnalysis) {
        return `${AppPagePaths.convoIntel}/${convoIntelPathResources.customAnalysis}`;
    }
    return "/";
};

export const getConvoIntelRoute = (flags: ConvoIntelFlags): RegalRouteObject => {
    const defaultPath = getDefaultConvoIntelPath(flags);

    return {
        path: AppPagePaths.convoIntel,
        element: <ConvoIntelRoutingPage />,
        hasNestedRoutes: true,
        children: [
            {
                index: true,
                element: <Navigate to={defaultPath} replace />,
            },
            {
                path: convoIntelPathResources.recordings,
                element: <TranscriptsList />,
            },
            {
                path: convoIntelPathResources.moments,
                element: <TrackersPage />,
            },
            {
                path: convoIntelPathResources.scorecards,
                element: <ScoreCardsList />,
            },
            {
                path: convoIntelPathResources.scorecardEdit,
                element: <ScoreCardsEditPage />,
            },
            {
                path: convoIntelPathResources.insights,
                element: <TranscriptInsights />,
            },
            {
                path: convoIntelPathResources.customAnalysis,
                element: <CustomAnalysis />,
            },
            {
                path: convoIntelPathResources.observability,
                element: <ObservabilityPage />,
            },
            {
                path: convoIntelPathResources.agentChats,
                element: <AgentChatsPage />,
            },
            {
                path: convoIntelPathResources.agentChatDetail,
                element: <AgentChatDetail />,
            },
            {
                path: convoIntelPathResources.edit,
                element: <TranscriptsPage />,
            },
            {
                path: convoIntelPathResources.taskEdit,
                element: <TranscriptsPage />,
            },
        ],
    };
};
